import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import { HomepageEditorRoute } from "./features/content/HomepageEditorRoute";
import {
  ADMIN_HOMEPAGE_CONTENT_PATH,
  isLegacyAdminHomepageContentPath,
} from "./features/content/homepage-editor-route-model";
import { usePortalSession } from "./features/content/usePortalSession";
import { RoleAccessRoute } from "./features/access/RoleAccessRoute";
import { renderPortalEmergencyFallback } from "./portal-emergency-fallback";
import { publishPortalRenderFailure } from "./portal-error-reporting";
import { PortalRootErrorBoundary } from "./PortalRootErrorBoundary";
import { PortalRuntimeNotices } from "./PortalRuntimeNotices";
import {
  startPortalLogoutSync,
  stopPortalLogoutSync,
} from "./protected-state.ts";
import { PORTAL_REACT_ROOT_BOUNDARY_ID } from "./route-error-model";
import { portalInstallPrompt } from "./install-prompt.ts";
import { portalServiceWorkerUpdates } from "./service-worker-updates.ts";
import "./styles.css";

function retireLegacyHomepageHashRoute() {
  const rawHash = window.location.hash.slice(1).replace(/^!+/u, "");
  if (rawHash === "") return;
  const hashPath = (rawHash.startsWith("/") ? rawHash : `/${rawHash}`).split(
    /[?&]/u,
    1,
  )[0];
  if (hashPath && isLegacyAdminHomepageContentPath(hashPath)) {
    window.location.replace(ADMIN_HOMEPAGE_CONTENT_PATH);
  }
}

retireLegacyHomepageHashRoute();
startPortalLogoutSync();
portalInstallPrompt.start();

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Portal root element is unavailable.");
}

let emergencyFallbackScheduled = false;
const handleUncaughtPortalError = () => {
  publishPortalRenderFailure({
    code: "REACT_UNCAUGHT_ERROR",
    boundaryId: PORTAL_REACT_ROOT_BOUNDARY_ID,
    routeId: "unknown",
    retryCount: 0,
  });

  if (emergencyFallbackScheduled) return;
  emergencyFallbackScheduled = true;
  queueMicrotask(() => {
    renderPortalEmergencyFallback(rootElement);
  });
};

const root = import.meta.env.PROD
  ? ReactDOM.createRoot(rootElement, {
      onCaughtError: () => undefined,
      onUncaughtError: handleUncaughtPortalError,
      onRecoverableError: () => {
        publishPortalRenderFailure({
          code: "REACT_RECOVERABLE_ERROR",
          boundaryId: PORTAL_REACT_ROOT_BOUNDARY_ID,
          routeId: "unknown",
          retryCount: 0,
        });
      },
    })
  : ReactDOM.createRoot(rootElement);

function PortalApplication() {
  const session = usePortalSession();
  return (
    <App
      subject={session.data?.subject ?? null}
      homepageEditorRoute={<HomepageEditorRoute session={session} />}
      roleAccessRoute={<RoleAccessRoute session={session} />}
      runtimeNotices={<PortalRuntimeNotices />}
      publicSiteHref="https://whitehavenwellness.com"
      privacyHref="https://whitehavenwellness.com/privacy"
    />
  );
}

root.render(
  <React.StrictMode>
    <PortalRootErrorBoundary>
      <PortalApplication />
    </PortalRootErrorBoundary>
  </React.StrictMode>,
);

const startPortalServiceWorkerUpdates = () => {
  void portalServiceWorkerUpdates.start();
};

if (import.meta.env.PROD) {
  if (document.readyState === "complete") {
    queueMicrotask(startPortalServiceWorkerUpdates);
  } else {
    window.addEventListener("load", startPortalServiceWorkerUpdates, {
      once: true,
    });
  }
}

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    window.removeEventListener("load", startPortalServiceWorkerUpdates);
    portalInstallPrompt.stop();
    portalServiceWorkerUpdates.stop();
    stopPortalLogoutSync();
  });
}
